/* eslint-disable @typescript-eslint/no-explicit-any */
import { IconButton } from "@/subframe/components/IconButton";
import { Button } from "@/subframe/components/Button";
import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import * as SubframeCore from "@subframe/core";
import { Switch } from "@/subframe/components/Switch";
import { TextArea } from "@/subframe/components/TextArea";
import { TextFieldUnstyled } from "@/subframe/components/TextFieldUnstyled";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import ReactCountryFlag from "react-country-flag";
import { getCountryCodeFromNumber } from "../Utils/countryNameToCode";
import { MessagesDataWithPagination } from "../Interfaces/messagesInterface";
import { getLatestReadMessage } from "../Utils/messagesHelper";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Alert } from "@/subframe/components/Alert";
import { useDispatch, useSelector } from "react-redux";
import { updateRecipient, getBots, deleteScheduledMessage, deleteLegacyScheduledMessage } from "../redux/reducers/messengerSlice";
import { AppDispatch, RootState } from "../redux/store";
import { formatPhoneNumber } from "../Utils/formatPhoneNumber";
import axios from "axios";
import { addEsChatToConfig } from "../Utils/apiUtils";
import { addLabel, removeLabel } from "../redux/reducers/authSlice";
import { Loader } from "@/subframe/components/Loader";
import { addNotification } from "../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../Utils/notificationEnums";
import { Accordion } from "@/subframe/components/Accordion";
import { ChatContactMenu } from "@/subframe/components/ChatContactMenu";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";

interface FriendInfoProps {
  currentRecipient: Recipient | null;
  messages: MessagesDataWithPagination;
  isSwitchOn: boolean;
  onToggle: () => void;
}

const normalizeGlobalLabels = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((label): label is string => typeof label === "string");
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return [];

    try {
      return normalizeGlobalLabels(JSON.parse(trimmedValue));
    } catch {
      return [trimmedValue];
    }
  }

  return [];
};

function FriendInfo({
  currentRecipient,
  messages,
  isSwitchOn,
  onToggle,
}: FriendInfoProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [lastReadStatus, setLastReadStatus] = useState<string | null>(null);
  const [name, setName] = useState(
    currentRecipient?.name || "No name available"
  );
  const nameRef = useRef<HTMLInputElement | null>(null);

  const [globalLabels, setGlobalLabels] = useState<string[]>(
    normalizeGlobalLabels(currentRecipient?.global_label)
  );
  const { labels } = useSelector((state: RootState) => state.auth);
  const { bots, botsLoading, scheduledMessages, scheduledMessagesLoading, legacyScheduledMessages, legacyScheduledMessagesLoading } = useSelector(
    (state: RootState) => state.messenger
  );
  const [label, setLabel] = useState("");
  const [loadingForLabelCreation, setLoadingForLabelCreation] = useState(false);
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [note, setNote] = useState(currentRecipient?.note || "");
  const [selectedBotId, setSelectedBotId] = useState<number | null>(
    currentRecipient?.ai_bot_id || null
  );
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [automationNoteExpanded, setAutomationNoteExpanded] = useState(false);
  const automationNoteRef = useRef<HTMLParagraphElement | null>(null);
  const [automationNoteOverflows, setAutomationNoteOverflows] = useState(false);
  const [isScheduleDetailsOpen, setIsScheduleDetailsOpen] = useState(false);
  const [selectedScheduledMessage, setSelectedScheduledMessage] = useState<any>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [pendingDeleteIsLegacy, setPendingDeleteIsLegacy] = useState(false);
  const scheduledCount = (scheduledMessages?.length ?? 0) + (legacyScheduledMessages?.length ?? 0);

  const formatConditionOne = (value: any): string => {
    if (value === null || value === undefined || value === "" || Number(value) <= 0) {
      return "-";
    }

    const raw = String(value).trim().toLowerCase();
    if (/^\d+[mhd]$/.test(raw)) {
      const amount = parseInt(raw.slice(0, -1), 10);
      const unit = raw.slice(-1);
      const unitLabel = unit === "m" ? "min" : unit === "h" ? "hours" : "day";
      return `If did not reply in ${amount} ${unitLabel}`;
    }

    const numericValue = Number(raw);
    if (Number.isNaN(numericValue) || numericValue <= 0) return "-";

    // API sends condition_one in milliseconds
    const totalMinutes = Math.floor(numericValue / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    let amount = totalMinutes;
    let unitLabel = "min";

    if (totalMinutes >= 1440 && totalMinutes % 1440 === 0) {
      amount = totalDays;
      unitLabel = "day";
    } else if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
      amount = totalHours;
      unitLabel = "hours";
    }

    return `If did not reply in ${amount} ${unitLabel}`;
  };

  const renderConditionOne = (value: any) => {
    const formatted = formatConditionOne(value);
    if (formatted === "-") {
      return <span className="text-body font-body text-default-font">No reply condition</span>;
    }

    return (
      <div className="flex w-full flex-col items-start gap-1">
        <span className="rounded-full bg-brand-100 px-2 py-0.5 text-caption font-caption text-brand-800">
          Reply condition enabled
        </span>
        <span className="text-body font-body text-default-font">{formatted}</span>
      </div>
    );
  };

  const getConditionTwoMeta = (value: any): { hasData: boolean; action: string; labels: string[] } => {
    if (!value || typeof value !== "object" || Object.keys(value).length === 0) {
      return { hasData: false, action: "-", labels: [] };
    }
    const action = value.c === "include" ? "include" : value.c === "not" ? "exclude" : String(value.c || "-");
    const labels = Array.isArray(value.v) ? value.v : [];
    return { hasData: labels.length > 0, action, labels };
  };

  const renderConditionTwo = (value: any) => {
    const conditionTwo = getConditionTwoMeta(value);
    if (!conditionTwo.hasData) {
      return <span className="text-body font-body text-default-font">No label condition</span>;
    }

    const isInclude = conditionTwo.action === "include";
    const actionText = isInclude ? "Include labels" : "Exclude labels";
    const actionClassName = isInclude
      ? "bg-success-100 text-success-700"
      : "bg-warning-100 text-warning-800";

    return (
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-caption font-caption ${actionClassName}`}>
            {actionText}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {conditionTwo.labels.length} label{conditionTwo.labels.length > 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex w-full flex-wrap items-start gap-1.5">
          {conditionTwo.labels.map((label) => (
            <span
              key={label}
              className="rounded-full border border-solid border-brand-200 bg-brand-50 px-2.5 py-1 text-caption font-caption text-brand-800"
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderScheduleType = (value: any, createdAt?: number) => {
    const type = String(value || "").toLowerCase();
    if (!type) {
      return <span className="text-body font-body text-default-font">-</span>;
    }

    const isSpecific = type === "specific";
    const label = isSpecific ? "Specific date & time" : "Relative delay";
    const createdAtText =
      createdAt && !Number.isNaN(Number(createdAt))
        ? moment(Number(createdAt)).local().format("MMM D, YYYY hh:mm A")
        : "-";
    const hint = isSpecific
      ? "Sent at an exact scheduled time"
      : `Sent after a duration from create time ${createdAtText}`;
    const badgeClassName = isSpecific
      ? "bg-brand-100 text-brand-800"
      : "bg-warning-100 text-warning-800";

    return (
      <div className="flex w-full flex-col items-start gap-1">
        <span className={`rounded-full px-2 py-0.5 text-caption font-caption ${badgeClassName}`}>
          {label}
        </span>
        <span className="text-caption font-caption text-subtext-color">{hint}</span>
      </div>
    );
  };

  const countryCode =
    currentRecipient && getCountryCodeFromNumber(currentRecipient?.phone_number.toString());

  // Get the time difference with Moment.js
  const getTimeDifference = (readTime: number | null) => {
    const currentTime = moment();
    const readMoment = moment(readTime);
    return readMoment.from(currentTime);
  };

  const latestReadMessage =
    messages && messages.items && getLatestReadMessage(messages.items);

  useEffect(() => {
    if (latestReadMessage) {
      const timeDifference = getTimeDifference(latestReadMessage.read_time);
      setLastReadStatus(timeDifference);
    } else {
      setLastReadStatus("No messages have been read.");
    }
  }, [latestReadMessage]);

  //Update Name
  const handleSaveName = () => {
    setIsNameEditing(false);
    if (
      currentRecipient &&
      currentRecipient.id &&
      name !== currentRecipient.name
    ) {
      const { id, manual_label, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          manual_label: Array.isArray(manual_label) ? manual_label : [],
          name,
        })
      );
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.SUCCESS,
          title: "Name updated successfully",
          message: "",
          icon: NotificationIcon.CHECK,
        })
      );
      nameRef.current?.blur();
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveName();
    }
  };

  //Update Note
  const handleNoteKeyDown = async (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      await saveNote();
      textAreaRef.current?.blur();
    }
  };

  const handleNoteBlur = async () => {
    if (isNoteEditing) {
      await saveNote();
    }
  };

  const saveNote = async () => {
    setIsNoteEditing(false);

    if (
      currentRecipient &&
      currentRecipient.id &&
      note !== currentRecipient?.note
    ) {
      const { id, manual_label, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          manual_label: Array.isArray(manual_label) ? manual_label : [],
          note,
        })
      );
    }
  };

  const handleDeleteScheduledMessage = (id?: number) => {
    if (!id) return;
    setPendingDeleteId(id);
    setPendingDeleteIsLegacy(false);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteLegacyScheduledMessage = (id?: number) => {
    if (!id) return;
    setPendingDeleteId(id);
    setPendingDeleteIsLegacy(true);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    setIsDeleteConfirmOpen(false);

    try {
      if (pendingDeleteIsLegacy) {
        await dispatch(deleteLegacyScheduledMessage({ id: pendingDeleteId })).unwrap();
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Scheduled Message Deleted",
            message: "The scheduled message has been deleted successfully.",
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        await dispatch(deleteScheduledMessage({ id: pendingDeleteId })).unwrap();
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Scheduled Message Deleted",
            message: "The scheduled message has been deleted successfully.",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }
      if (selectedScheduledMessage?.id === pendingDeleteId) {
        setIsScheduleDetailsOpen(false);
        setSelectedScheduledMessage(null);
      }
    } catch (error: any) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Delete Failed",
          message: error?.message || "Could not delete scheduled message.",
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setPendingDeleteId(null);
    }
  };

  useEffect(() => {
    setNote(currentRecipient?.note || "");
  }, [currentRecipient?.note]);

  useEffect(() => {
    setName(currentRecipient?.name || "");
  }, [currentRecipient?.name]);

  // Fetch bots on component mount
  useEffect(() => {
    dispatch(getBots());
  }, [dispatch]);

  // Update selected bot when current recipient changes
  useEffect(() => {
    setSelectedBotId(currentRecipient?.ai_bot_id || null);
  }, [currentRecipient?.ai_bot_id]);

  // Reset automation note expand when switching contact
  useEffect(() => {
    setAutomationNoteExpanded(false);
  }, [currentRecipient?.id]);

  // Measure if automation note content overflows 2 lines (to show "See more" only when needed)
  useEffect(() => {
    if (!currentRecipient?.note_auto || automationNoteExpanded) {
      setAutomationNoteOverflows(false);
      return;
    }
    const el = automationNoteRef.current;
    if (!el) return;
    const overflow = el.scrollHeight > el.clientHeight;
    setAutomationNoteOverflows(overflow);
  }, [currentRecipient?.note_auto, automationNoteExpanded]);

  const handleRemoveGlobalLabel = (labelToRemove: string) => {
    const updatedLabels = globalLabels?.filter(
      (label) => label !== labelToRemove
    );

    setGlobalLabels(updatedLabels);

    if (currentRecipient && currentRecipient.id) {
      const { id, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          global_label: updatedLabels,
        })
      );
    }
  };

  useEffect(() => {
    setGlobalLabels(normalizeGlobalLabels(currentRecipient?.global_label));
  }, [currentRecipient?.global_label]);

  // const handleRemoveManualLabel = (labelToRemove: string) => {
  //   const updatedLabels = manualLabels?.filter(
  //     (label) => label !== labelToRemove
  //   );

  //   setManualLabels(updatedLabels);

  //   if (currentRecipient && currentRecipient.id) {
  //     const { id, ...rest } = currentRecipient;
  //     dispatch(
  //       updateRecipient({
  //         id,
  //         ...rest,
  //         manual_label: updatedLabels,
  //       })
  //     );
  //   }
  // };

  const handleAssignLabel = (label: string) => {
    if (currentRecipient && currentRecipient.id) {
      if (globalLabels.includes(label)) {
        return;
      }

      const updatedLabels = [...(globalLabels || []), label];

      const { id, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          global_label: updatedLabels,
        })
      );
      setGlobalLabels(updatedLabels);
    }
  };

  const handleCreateLabel = async () => {
    if (!label.trim()) {
      return;
    }
    setLoadingForLabelCreation(true);

    try {
      const config = addEsChatToConfig();

      const response = await axios.post(
        "https://api.insightssystem.com/api:bVXsw_FD/web/label_management",
        {
          label,
          keywords: [],
        },
        config
      );

      dispatch(addLabel(response.data));
      setLoadingForLabelCreation(true);
      setLabel("");
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to create label. Please try again.";
      console.error("Label creation error:", errorMessage);

      // Show error notification
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Label Creation Failed",
          message: errorMessage,
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setLoadingForLabelCreation(false);
    }
  };

  const handleDeleteLabel = async (labelId: number) => {
    if (!labelId) return;

    try {
      const config = addEsChatToConfig();

      await axios.delete(
        `https://api.insightssystem.com/api:bVXsw_FD/web/label_management/${labelId}`,
        config
      );
      dispatch(removeLabel(labelId));
    } catch (err) {
      console.error("Label deletion error:", err);
    }
  };

  const handleBotAssignment = (botId: number | null) => {
    if (currentRecipient && currentRecipient.id) {
      const { id, manual_label, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          manual_label: Array.isArray(manual_label) ? manual_label : [],
          ai_bot_id: botId,
        })
      );
      setSelectedBotId(botId);

      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.SUCCESS,
          title: "Bot assignment updated",
          message: botId ? "Bot has been assigned successfully" : "Bot has been unassigned",
          icon: NotificationIcon.CHECK,
        })
      );
    }
  };

  return (
    <>
      {/* Right part - fixed height, only inner content scrolls */}
      <div className="flex h-full min-h-0 min-w-0 w-full flex-none flex-col overflow-hidden border-l border-solid border-neutral-border">
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
            <div className="flex w-full flex-col items-start gap-6">
              <div className="conveythis-no-translate flex items-start gap-3">
                <div className="flex flex-col flex-wrap items-center justify-center gap-2 relative">
                  <svg
                    className="h-12 w-12 flex-none rounded-full"
                    xmlns="http://www.w3.org/2000/svg"
                    width="401px"
                    height="401px"
                    enable-background="new 312.809 0 401 401"
                    version="1.1"
                    viewBox="312.809 0 401 401"
                  >
                    <g transform="matrix(1.223 0 0 1.223 -467.5 -843.44)">
                      <rect
                        x="601.45"
                        y="653.07"
                        width="401"
                        height="401"
                        fill="#E4E6E7"
                      />
                      <path
                        d="m802.38 908.08c-84.515 0-153.52 48.185-157.38 108.62h314.79c-3.87-60.44-72.9-108.62-157.41-108.62z"
                        fill="#AEB4B7"
                      />
                      <path
                        d="m881.37 818.86c0 46.746-35.106 84.641-78.41 84.641s-78.41-37.895-78.41-84.641 35.106-84.641 78.41-84.641c43.31 0 78.41 37.9 78.41 84.64z"
                        fill="#AEB4B7"
                      />
                    </g>
                  </svg>
                  <div className="flex h-7 w-7 flex-none items-center rounded-full pl-3 pt-2 absolute -bottom-2">
                    <div className="flex items-center gap-3.5">
                      <div>
                        <div className=" flex items-center rounded-full border-[3px] border-solid border-red h-5 w-5 flex-none bg-white">
                          <ReactCountryFlag
                            style={{
                              width: "5em",
                              height: "5em",
                            }}
                            countryCode={countryCode?.countryCode ? countryCode.countryCode : "US"}
                            svg
                          />
                        </div>
                      </div>
                      <span className="min-w-[176px] grow shrink-0 basis-0 text-caption font-caption text-subtext-color">
                        {currentRecipient && currentRecipient.country
                          ? currentRecipient.country
                          : "No country name available"}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className="flex flex-col items-start"
                  title={!isNameEditing ? "Tap to edit name" : ""}
                >
                  <TextFieldUnstyled>
                    <TextFieldUnstyled.Input
                      ref={nameRef}
                      placeholder="No name available"
                      value={name}
                      onFocus={() => setIsNameEditing(true)}
                      onBlur={handleSaveName}
                      onChange={(e) => setName(e.target.value)}
                      onKeyDown={handleNameKeyDown}
                      style={{
                        cursor: isNameEditing ? "text" : "pointer",
                        outline: "none",
                        border: isNameEditing ? "1px solid #007bff" : "none",
                        borderRadius: "4px",
                        backgroundColor: isNameEditing
                          ? "#f8f9fa"
                          : "transparent",
                        width: "100%",
                      }}
                      onClick={() => {
                        if (!isNameEditing) {
                          setIsNameEditing(true);
                        }
                      }}
                    />
                  </TextFieldUnstyled>
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    {countryCode?.countryCode && currentRecipient?.phone_number
                      ? formatPhoneNumber(
                        currentRecipient.phone_number,
                        countryCode.countryCode
                      )
                      : currentRecipient?.phone_number
                        ? currentRecipient.phone_number
                        : "No number available"}
                    {/* {currentRecipient?.phone_number} */}
                  </span>
                </div>
              </div>
              <div className="conveythis-no-translate flex w-full flex-col items-start gap-1">
                <span className="text-caption-bold font-caption-bold text-subtext-color">
                  Last Read
                </span>
                <span className="text-body font-body text-default-font">
                  {lastReadStatus}
                </span>
              </div>
              <div className="conveythis-no-translate flex w-full flex-col items-start gap-1">
                <div className="flex items-center">
                  <span className="text-caption-bold font-caption-bold text-subtext-color">
                    Label
                  </span>
                  <IconButton
                    size="small"
                    icon="FeatherSettings"
                  // onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                  />
                </div>
                <div className="flex w-full flex-wrap items-center gap-2">
                  <SubframeCore.Icon
                    className="text-caption font-caption text-neutral-700"
                    name={null}
                  />
                  {globalLabels?.map((label, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1 rounded-md border border-neutral-border bg-neutral-100 pl-2 pr-1 py-0.5"
                    >
                      <span className="whitespace-nowrap text-caption font-caption text-neutral-700">
                        {label}
                      </span>
                      <IconButton
                        size="small"
                        icon="FeatherX"
                        onClick={() => handleRemoveGlobalLabel(label)}
                      />
                    </div>
                  ))}
                  <SubframeCore.Icon
                    className="text-caption font-caption text-neutral-700"
                    name={null}
                  />
                  <SubframeCore.DropdownMenu.Root>
                    <SubframeCore.DropdownMenu.Trigger asChild={true}>
                      <Button
                        className="h-8 grow shrink-0 basis-0"
                        variant="neutral-secondary"
                        iconRight="FeatherChevronDown"
                      // onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                      >
                        Create/Assign
                      </Button>
                    </SubframeCore.DropdownMenu.Trigger>
                    <SubframeCore.DropdownMenu.Portal>
                      <SubframeCore.DropdownMenu.Content
                        side="bottom"
                        align="start"
                        sideOffset={2}
                        asChild={true}
                      >
                        <DropdownMenu className="flex max-h-[320px] min-w-[220px] flex-col overflow-hidden">
                          <div className="flex shrink-0 w-full items-center gap-1">
                            <TextFieldUnstyled className="min-w-0 flex-1">
                              <TextFieldUnstyled.Input
                                className="h-8 w-full min-w-0 flex-none"
                                placeholder="Create Label "
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                onKeyDown={(e) => {
                                  // Prevent dropdown type-ahead from stealing focus when typing in the input
                                  if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                                    e.stopPropagation();
                                  }
                                }}
                                disabled={loadingForLabelCreation}
                              />
                            </TextFieldUnstyled>
                            {loadingForLabelCreation ? (
                              <Loader size="small" />
                            ) : (
                              <IconButton
                                variant="inverse"
                                size="small"
                                icon="FeatherCheck"
                                onClick={handleCreateLabel}
                                disabled={loadingForLabelCreation}
                              />
                            )}
                          </div>
                          <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          <div className="min-h-0 max-h-[240px] w-full flex-1 overflow-y-auto overflow-x-hidden">
                            {labels?.map((labelItem) => {
                              // Check if the label is already in global_label array
                              const isAssigned = globalLabels.includes(
                                labelItem.label
                              );

                              return (
                                <div
                                  key={labelItem.id}
                                  className="flex w-full items-center gap-2"
                                >
                                  <DropdownMenu.DropdownItem
                                    icon={null}
                                    className={`min-w-0 flex-1 ${isAssigned
                                        ? "pointer-events-none bg-gray-100"
                                        : ""
                                      }`}
                                    onClick={() =>
                                      handleAssignLabel(labelItem.label)
                                    }
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <span>{labelItem.label}</span>
                                      {isAssigned && (
                                        <span className="text-gray-500 text-sm ml-2">
                                          Already assigned
                                        </span>
                                      )}
                                    </div>
                                  </DropdownMenu.DropdownItem>
                                  <IconButton
                                    size="small"
                                    icon="FeatherX"
                                    className="text-red-500"
                                    onClick={() => handleDeleteLabel(labelItem.id)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </DropdownMenu>
                      </SubframeCore.DropdownMenu.Content>
                    </SubframeCore.DropdownMenu.Portal>
                  </SubframeCore.DropdownMenu.Root>
                </div>
              </div>
              <div className="flex w-full flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-2.5 py-2 shadow-sm">
                <span className="text-caption-bold font-caption-bold text-subtext-color">
                  Chat Handoff
                </span>
                <div className="flex w-full items-center justify-center gap-2">
                  <SubframeCore.Icon
                    className="text-caption font-caption text-neutral-700"
                    name={null}
                  />
                  <SubframeCore.Icon
                    className="text-caption font-caption text-neutral-700"
                    name={null}
                  />
                  <span className="grow shrink-0 basis-0 text-body font-body text-default-font text-center">
                    Human Agent
                  </span>
                  <Switch checked={isSwitchOn} onCheckedChange={onToggle} />
                  <span className="grow shrink-0 basis-0 text-body font-body text-default-font text-center">
                    AI Assistant
                  </span>
                </div>
              </div>
              <Alert
                title="AI Assistant"
                description={
                  "Enabled: Messages will be shared with your connected automation tools (Zapier, Make, etc.)\nDisabled: Messages won't sync with automation tools"
                }
              />
              <div className="conveythis-no-translate flex w-full flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-2.5 py-2 shadow-sm">
                <span className="text-caption-bold font-caption-bold text-subtext-color">
                  Bot Assign
                </span>
                <div className="flex w-full items-center gap-2">
                  {botsLoading ? (
                    <div className="flex w-full items-center justify-center py-2">
                      <Loader size="small" />
                    </div>
                  ) : (
                    <SubframeCore.DropdownMenu.Root>
                      <SubframeCore.DropdownMenu.Trigger asChild={true}>
                        <Button
                          className="h-8 w-full"
                          variant="neutral-secondary"
                          iconRight="FeatherChevronDown"
                        >
                          {selectedBotId
                            ? bots.find((bot) => bot.id === selectedBotId)?.bot_name || "Select Bot"
                            : "Select Bot"}
                        </Button>
                      </SubframeCore.DropdownMenu.Trigger>
                      <SubframeCore.DropdownMenu.Portal>
                        <SubframeCore.DropdownMenu.Content
                          side="bottom"
                          align="start"
                          sideOffset={2}
                          asChild={true}
                        >
                          <DropdownMenu>
                            {selectedBotId && (
                              <>
                                <DropdownMenu.DropdownItem
                                  icon="FeatherX"
                                  onClick={() => handleBotAssignment(null)}
                                >
                                  Unassign Bot
                                </DropdownMenu.DropdownItem>
                                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                              </>
                            )}
                            {bots?.length > 0 ? (
                              bots.map((bot) => (
                                <DropdownMenu.DropdownItem
                                  key={bot.id}
                                  icon={selectedBotId === bot.id ? "FeatherCheck" : null}
                                  onClick={() => handleBotAssignment(bot.id)}
                                  className={selectedBotId === bot.id ? "bg-neutral-50" : ""}
                                >
                                  {bot.bot_name}
                                </DropdownMenu.DropdownItem>
                              ))
                            ) : (
                              <DropdownMenu.DropdownItem icon={null}>
                                No bots available
                              </DropdownMenu.DropdownItem>
                            )}
                          </DropdownMenu>
                        </SubframeCore.DropdownMenu.Content>
                      </SubframeCore.DropdownMenu.Portal>
                    </SubframeCore.DropdownMenu.Root>
                  )}
                </div>
              </div>
            </div>
            <Accordion
              trigger={
                <div className="flex w-full items-center gap-2 py-2">
                  <div className="flex grow shrink-0 basis-0 items-center gap-2">
                    <span className="text-body-bold font-body-bold text-subtext-color">
                      Scheduled Messages
                    </span>
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-caption font-caption text-subtext-color">
                      {scheduledCount}
                    </span>
                  </div>
                  <Accordion.Chevron />
                  {/* <IconButton
                    variant="brand-secondary"
                    size="small"
                    icon="FeatherPlus"
                    onClick={() => {}}
                  /> */}
                </div>
              }
              defaultOpen={false}
            >
              <div className="conveythis-no-translate flex w-full grow shrink-0 basis-0 flex-col items-start gap-2">
                <div className="flex h-52 w-full flex-none flex-col items-start gap-2 overflow-auto rounded-md border border-solid border-neutral-border bg-neutral-50 px-2 py-2">
                  {(scheduledMessagesLoading || legacyScheduledMessagesLoading) ? (
                    <div className="flex w-full items-center justify-center py-4">
                      <Loader size="small" />
                    </div>
                  ) : (scheduledMessages?.length > 0 || legacyScheduledMessages?.length > 0) ? (
                    <>
                      {scheduledMessages?.length > 0 && scheduledMessages.map((item: any, index: number) => {
                        const rawScheduleTime =
                          item?.schedule_time || item?.relative_schedule_time || item?.specific_schedule_time;
                        const scheduleTimestamp = rawScheduleTime ? Number(rawScheduleTime) : null;
                        const computedStatus =
                          scheduleTimestamp && Date.now() >= scheduleTimestamp ? "sent" : "pending";
                        const scheduleTypeLabel =
                          String(item?.schedule_type || "").toLowerCase() === "specific"
                            ? "Specific"
                            : "Relative";
                        return (
                          <ChatContactMenu.ScheduledMessage
                            key={`new-${item?.id || index}`}
                            message={item?.body || item?.message || item?.text || "Scheduled message"}
                            scheduledTime={
                              scheduleTimestamp
                                ? (
                                  <span className="flex items-center gap-2">
                                    <span>{moment(scheduleTimestamp).local().format("MMM D, YYYY hh:mm A")}</span>
                                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-[600] leading-[14px] text-subtext-color">
                                      {scheduleTypeLabel}
                                    </span>
                                  </span>
                                )
                                : "-"
                            }
                            status={computedStatus}
                            onView={() => {
                              setSelectedScheduledMessage({ ...item, _isLegacy: false });
                              setIsScheduleDetailsOpen(true);
                            }}
                            onDelete={() => handleDeleteScheduledMessage(item?.id)}
                          />
                        );
                      })}
                      {legacyScheduledMessages?.length > 0 && (
                        <>
                          {/* <div className="flex w-full items-center gap-1.5 px-1 pt-1 pb-0.5">
                            <SubframeCore.Icon
                              className="flex-none text-[10px] text-neutral-400"
                              name="FeatherArchive"
                            />
                            <span className="text-[10px] font-semibold text-neutral-400">
                              From Legacy System
                            </span>
                            <div className="h-px flex-1 bg-neutral-200" />
                          </div> */}
                          {legacyScheduledMessages.map((item: any, index: number) => {
                            const scheduleTimestamp = item?.schedule ? Number(item.schedule) : null;
                            const computedStatus =
                              scheduleTimestamp && Date.now() >= scheduleTimestamp ? "sent" : "pending";
                            return (
                              <ChatContactMenu.ScheduledMessage
                                key={`legacy-${item?.id || index}`}
                                message={item?.body || "Scheduled message"}
                                scheduledTime={
                                  scheduleTimestamp
                                    ? (
                                      <span className="flex items-center gap-2">
                                        <span>{moment(scheduleTimestamp).local().format("MMM D, YYYY hh:mm A")}</span>
                                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-[600] leading-[14px] text-blue-500">
                                          Specific
                                        </span>
                                      </span>
                                    )
                                    : "-"
                                }
                                status={computedStatus}
                                onView={() => {
                                  setSelectedScheduledMessage({ ...item, _isLegacy: true });
                                  setIsScheduleDetailsOpen(true);
                                }}
                                onDelete={() => handleDeleteLegacyScheduledMessage(item?.id)}
                              />
                            );
                          })}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex w-full items-center justify-center py-4">
                      <span className="text-caption font-caption text-subtext-color text-center">
                        No scheduled messages
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Accordion>
            <div className="conveythis-no-translate flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Notes
              </span>
              <TextArea
                className="h-auto w-full flex-none"
                variant="filled"
                label=""
                helpText=""
              >
                <TextArea.Input
                  ref={textAreaRef}
                  className="min-h-[96px]"
                  placeholder="Add notes about this contact..."
                  value={note}
                  onFocus={() => setIsNoteEditing(true)}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={handleNoteBlur}
                  onKeyDown={handleNoteKeyDown}
                />
              </TextArea>
            </div>
            {currentRecipient?.note_auto && (
              <div className="conveythis-no-translate flex w-full flex-col items-start gap-2">
                <div className="flex w-full flex-col items-start gap-1">
                  <div className="w-full rounded-md border border-neutral-border bg-neutral-50 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-blue-600">Automation Note</span>
                    </div>
                    <p
                      ref={automationNoteRef}
                      className={`text-sm text-gray-600 whitespace-pre-wrap ${!automationNoteExpanded ? "line-clamp-2" : ""
                        }`}
                    >
                      {currentRecipient.note_auto}
                    </p>
                    {(automationNoteOverflows || automationNoteExpanded) && (
                      <button
                        type="button"
                        onClick={() => setAutomationNoteExpanded((prev) => !prev)}
                        className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:ring-0"
                      >
                        {automationNoteExpanded ? "See less" : "See more"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DialogLayout open={isScheduleDetailsOpen} onOpenChange={setIsScheduleDetailsOpen}>
        <div className="flex w-full max-w-[620px] flex-col items-start rounded-md bg-default-background shadow-lg">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-4 py-3">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Scheduled Message Details
            </span>
            <IconButton
              variant="neutral-tertiary"
              size="small"
              icon="FeatherX"
              onClick={() => setIsScheduleDetailsOpen(false)}
            />
          </div>
          <div className="conveythis-no-translate flex w-full flex-col items-start gap-4 px-4 py-4">
            <div className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-neutral-50 px-3 py-2">
              <span className="text-caption-bold font-caption-bold text-subtext-color">Status</span>
              <span className="rounded-full bg-success-100 px-2 py-0.5 text-caption font-caption text-success-700">
                {(() => {
                  const ts = selectedScheduledMessage?._isLegacy
                    ? selectedScheduledMessage?.schedule
                    : selectedScheduledMessage?.schedule_time;
                  return ts && Date.now() >= Number(ts) ? "sent" : "pending";
                })()}
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-1 rounded-md border border-solid border-neutral-border px-3 py-3">
              <span className="text-caption-bold font-caption-bold text-subtext-color">Message Body</span>
              <span className="whitespace-pre-wrap text-body font-body text-default-font leading-relaxed">
                {selectedScheduledMessage?.body || selectedScheduledMessage?.message || "-"}
              </span>
            </div>
            <div className="grid w-full grid-cols-2 gap-3">
              <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border px-3 py-2">
                <span className="text-caption-bold font-caption-bold text-subtext-color">Schedule Type</span>
                {renderScheduleType(
                  selectedScheduledMessage?._isLegacy ? "specific" : selectedScheduledMessage?.schedule_type,
                  selectedScheduledMessage?.created_at
                )}
              </div>
              <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border px-3 py-2">
                <span className="text-caption-bold font-caption-bold text-subtext-color">Schedule Time</span>
                <span className="text-body font-body text-default-font">
                  {(() => {
                    const ts = selectedScheduledMessage?._isLegacy
                      ? selectedScheduledMessage?.schedule
                      : selectedScheduledMessage?.schedule_time;
                    return ts ? moment(Number(ts)).local().format("MMM D, YYYY hh:mm A") : "-";
                  })()}
                </span>
              </div>
              {!selectedScheduledMessage?._isLegacy && (
                <>
                  <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border px-3 py-2">
                    <span className="text-caption-bold font-caption-bold text-subtext-color">Condition One</span>
                    {renderConditionOne(selectedScheduledMessage?.condition_one)}
                  </div>
                  <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border px-3 py-2">
                    <span className="text-caption-bold font-caption-bold text-subtext-color">Condition Two</span>
                    {renderConditionTwo(selectedScheduledMessage?.condition_two)}
                  </div>
                </>
              )}
              {selectedScheduledMessage?._isLegacy && selectedScheduledMessage?.channel && typeof selectedScheduledMessage.channel === "string" && (
                <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border px-3 py-2">
                  <span className="text-caption-bold font-caption-bold text-subtext-color">Channel</span>
                  <span className="text-body font-body text-default-font">
                    {selectedScheduledMessage.channel}
                  </span>
                </div>
              )}
              {selectedScheduledMessage?._isLegacy && (() => {
                const raw = selectedScheduledMessage?.labels;
                if (!raw) return null;
                const labelsText = Array.isArray(raw)
                  ? raw.join(", ")
                  : typeof raw === "string"
                  ? raw
                  : null;
                if (!labelsText) return null;
                return (
                  <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border px-3 py-2">
                    <span className="text-caption-bold font-caption-bold text-subtext-color">Labels</span>
                    <span className="text-body font-body text-default-font">{labelsText}</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </DialogLayout>

      <DialogLayout open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <div className="flex w-96 flex-col items-start gap-6 px-6 py-6">
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Delete Scheduled Message?
            </span>
            <span className="font-['Inter'] text-[13px] font-[400] leading-[19px] text-subtext-color">
              This action cannot be undone. The scheduled message will be
              permanently deleted and will not be sent to any recipients.
            </span>
          </div>
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              className="h-8 grow shrink-0 basis-0"
              variant="neutral-secondary"
              size="medium"
              icon={null}
              iconRight={null}
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="h-8 grow shrink-0 basis-0"
              variant="destructive-primary"
              size="medium"
              icon="FeatherTrash"
              iconRight={null}
              onClick={handleConfirmDelete}
            >
              Delete Message
            </Button>
          </div>
        </div>
      </DialogLayout>
    </>
  );
}

export default FriendInfo;
